// pages/OTPVerificationPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import API from '../api';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/global.css';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// ✅ Import useAuth to update auth state
import { useAuth } from '../context/AuthContext';

export default function OTPVerificationPage() {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [timer, setTimer] = useState(600);
  const [canResend, setCanResend] = useState(false);
  const [mounted, setMounted] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const inputRefs = useRef([]);

  // ✅ Get fetchUser from AuthContext
  const { fetchUser } = useAuth();

  useEffect(() => {
    setMounted(true);
    if (inputRefs.current[0]) inputRefs.current[0].focus();
  }, []);

  useEffect(() => {
    if (!email) {
      toast.error('No email found. Please register first.');
      navigate('/register');
    }
  }, [email, navigate]);

  useEffect(() => {
    if (timer <= 0) {
      setCanResend(true);
      return;
    }
    const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  useEffect(() => {
    const otpString = otp.join('');
    if (otpString.length === 6 && /^\d{6}$/.test(otpString)) {
      handleVerify(otpString);
    }
  }, [otp]);

  const handleChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');
    if (value && index < 5) inputRefs.current[index + 1].focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then((text) => {
        const digits = text.replace(/\D/g, '').slice(0, 6).split('');
        const newOtp = [...otp];
        digits.forEach((d, i) => {
          if (i < 6) newOtp[i] = d;
        });
        setOtp(newOtp);
        if (digits.length === 6 && inputRefs.current[5]) inputRefs.current[5].focus();
      });
    }
  };

  const handleVerify = async (otpString = otp.join('')) => {
    if (loading) return;
    if (otpString.length !== 6) {
      toast.error('Please enter all 6 digits');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const response = await API.post('/users/verify-otp', { email, otp: otpString });
      
      // ✅ Step 1: Save token to localStorage
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userRole', response.data.role);

      // ✅ Step 2: Update AuthContext by fetching user profile
      // This sets user state so RequireAuth won't redirect to /login
      await fetchUser();

      // ✅ Step 3: Show success toast
      toast.success('Email verified successfully! Redirecting to dashboard...', {
        position: 'top-center',
        autoClose: 2000,
      });

      // ✅ Step 4: Navigate to dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);

    } catch (err) {
      const msg = err.response?.data?.message || 'Invalid OTP.';
      setError(msg);
      toast.error(msg);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0].focus();
    }
    setLoading(false);
  };

  const handleResend = async () => {
    if (resendLoading || !canResend) return;
    setResendLoading(true);
    setError('');

    try {
      await API.post('/users/resend-otp', { email });

      toast.success('New OTP sent to your email!', { position: 'top-center' });

      setTimer(600);
      setCanResend(false);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0].focus();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to resend OTP';
      setError(msg);
      toast.error(msg);
    }
    setResendLoading(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        .page-container {
          min-height: 100vh;
          display: flex;
          font-family: 'Poppins', sans-serif;
          background-color: #1a2b3c;
          background-image: radial-gradient(circle at 10% 20%, rgba(4, 159, 137, 0.1) 0%, transparent 20%),
                            radial-gradient(circle at 90% 80%, rgba(4, 159, 137, 0.1) 0%, transparent 20%);
          overflow: hidden;
          position: relative;
        }
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
        @keyframes slideUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        
        .otp-input:focus {
          border-color: #81e6d9 !important;
          box-shadow: 0 0 0 4px rgba(129, 230, 217, 0.2);
          transform: translateY(-2px);
        }
        
        @media (max-width: 900px) {
          .left-panel { display: none !important; }
          .right-panel { width: 100% !important; padding: 20px !important; }
        }
      `}</style>

      <ToastContainer />

      <div className="page-container">
        {/* LEFT PANEL */}
        <div className="left-panel" style={styles.leftPanel}>
          <div style={styles.illustrationContainer}>
            <img
              src="https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Envelope.png"
              alt="OTP 3D"
              style={styles.heroImage}
            />
            <div style={styles.heroTextContainer}>
              <h1 style={styles.heroTitle}>Check Your Inbox</h1>
              <p style={styles.heroSubtitle}>
                We've sent a 6-digit verification code to <br />
                <span style={{ color: '#81e6d9', fontWeight: 600 }}>{email}</span>
              </p>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="right-panel" style={styles.rightPanel}>
          <div
            style={{
              ...styles.formCard,
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(20px)',
            }}
          >
            <div style={styles.headerSection}>
              <div style={styles.iconCircle}>🔐</div>
              <h2 style={styles.headerTitle}>Verify OTP</h2>
              <p style={styles.headerSubtitle}>Enter the code below to verify</p>
            </div>

            <div style={styles.otpContainer}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="otp-input"
                  style={styles.otpInput}
                  disabled={loading}
                />
              ))}
            </div>

            {error && (
              <div style={styles.errorBox}>
                <span>⚠</span> {error}
              </div>
            )}

            <div style={styles.timerText}>
              {timer > 0 ? (
                <>
                  Code expires in{' '}
                  <span style={{ color: '#81e6d9', fontWeight: 600 }}>{formatTime(timer)}</span>
                </>
              ) : (
                <span style={{ color: '#fc8181' }}>Code expired</span>
              )}
            </div>

            <button
              onClick={() => handleVerify()}
              disabled={loading || otp.join('').length !== 6}
              style={styles.submitBtn(loading || otp.join('').length !== 6)}
            >
              {loading ? 'VERIFYING...' : 'VERIFY EMAIL'}
            </button>

            <div style={styles.resendContainer}>
              Didn't receive code?{' '}
              <button
                onClick={handleResend}
                disabled={!canResend || resendLoading}
                style={styles.resendBtn(canResend)}
              >
                {resendLoading ? 'Sending...' : 'Resend OTP'}
              </button>
            </div>

            <div style={styles.footerLink}>
              <button onClick={() => navigate('/register')} style={styles.backBtn}>
                ← Incorrect Email?
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const styles = {
  leftPanel: {
    width: '55%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 1,
  },
  rightPanel: {
    width: '25%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 2,
  },
  illustrationContainer: {
    width: '100%',
    maxWidth: '600px',
    padding: '40px',
    textAlign: 'center',
  },
  heroImage: {
    width: '280px',
    height: 'auto',
    animation: 'float 6s ease-in-out infinite',
    marginBottom: '30px',
    filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.5))',
  },
  heroTextContainer: { animation: 'slideUp 0.8s ease-out' },
  heroTitle: {
    color: '#fff',
    fontSize: '36px',
    fontWeight: '700',
    marginBottom: '15px',
    lineHeight: 1.2,
  },
  heroSubtitle: {
    color: '#a0aec0',
    fontSize: '16px',
    lineHeight: 1.6,
    maxWidth: '450px',
    margin: '0 auto',
  },
  formCard: {
    width: '100%',
    maxWidth: '480px',
    background: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: '30px',
    padding: '45px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    transition: 'all 0.5s ease',
  },
  headerSection: { marginBottom: '30px', textAlign: 'center' },
  iconCircle: { fontSize: '40px', marginBottom: '10px' },
  headerTitle: { color: '#fff', fontSize: '28px', fontWeight: '600', margin: 0 },
  headerSubtitle: { color: '#a0aec0', fontSize: '15px', marginTop: '5px' },
  otpContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '25px',
  },
  otpInput: {
    width: '50px',
    height: '60px',
    fontSize: '24px',
    fontWeight: 'bold',
    textAlign: 'center',
    background: 'rgba(0, 0, 0, 0.25)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    color: '#fff',
    outline: 'none',
    transition: 'all 0.2s',
  },
  timerText: {
    textAlign: 'center',
    marginBottom: '20px',
    fontSize: '14px',
    color: '#cbd5e0',
  },
  submitBtn: (disabled) => ({
    width: '100%',
    padding: '16px',
    background: disabled ? '#4a5568' : '#81e6d9',
    color: disabled ? '#a0aec0' : '#1a202c',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: disabled ? 'not-allowed' : 'pointer',
    letterSpacing: '1px',
    boxShadow: disabled
      ? 'none'
      : '0 4px 14px 0 rgba(129, 230, 217, 0.39)',
    transition: 'all 0.2s',
  }),
  resendContainer: {
    textAlign: 'center',
    marginTop: '20px',
    fontSize: '14px',
    color: '#a0aec0',
  },
  resendBtn: (active) => ({
    background: 'none',
    border: 'none',
    color: active ? '#81e6d9' : '#4a5568',
    cursor: active ? 'pointer' : 'not-allowed',
    textDecoration: active ? 'underline' : 'none',
    fontWeight: '600',
    marginLeft: '5px',
  }),
  footerLink: { textAlign: 'center', marginTop: '20px' },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#718096',
    cursor: 'pointer',
    fontSize: '13px',
  },
  errorBox: {
    background: 'rgba(229, 62, 62, 0.2)',
    color: '#feb2b2',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '20px',
    textAlign: 'center',
  },
};